import { useContext } from "react";
import { Menu } from "@headlessui/react";
import { assetIsPreviewable } from "../preview/utils";
import {
  FaDownload,
  FaEye,
  FaEyeSlash,
  FaEllipsisVertical,
  FaLocationCrosshairs,
} from "react-icons/fa6";
import prettyBytes from "pretty-bytes";
import {
  prepAssetDownloadURL,
  prepAssetPreviewURL,
} from "../../apiUtils/downloadUrls";
import Card from "./card";
import { formatUtcDate } from "../../utils";
import { iconForFileType } from "../../utils/cardUtil";
import MatchSealogEventsDisplay from "../dataAsset/matchSealogEventsDisplay";
import { hasFeatureFlag } from "../../utils";
import UserContext from "../../contexts/user";
import { AvailableFeatureFlags } from "../../apiClient";
import { useNavigate } from "@tanstack/react-router";
import Skeleton from "../loaders/Skeleton";
import { retrieveSealogTimestamps } from "../dataAsset/retrieveSealogTimestamps";
import useDataAssetSealogSearch from "@/hooks/useDataAssetSealogSearch";
import { StorageTier } from "../../apiClient/models/StorageTier";
import { StorageTierStatus } from "../../apiClient/models/StorageTierStatus";
import type { DataAssetManifestLinkTierResponse } from "../../apiClient/models/DataAssetManifestLinkTierResponse";

type StorageTierState = "archived" | "rehydrating" | null;

// Mirrors the backend's raise_if_blob_unreadable per link: unreadable iff
// the blob's applied (physical) tier is archive — rehydrating links are
// still archive-applied. NULL = never applied by us = hot = readable.
const linkIsUnreadable = (link: DataAssetManifestLinkTierResponse): boolean =>
  link.applied_storage_tier === StorageTier.ARCHIVE;

const getStorageTierState = (dataAsset: {
  manifest_links?: DataAssetManifestLinkTierResponse[];
}): StorageTierState => {
  const activeLinks = (dataAsset.manifest_links ?? []).filter(
    (link) => link.active
  );

  // The download endpoint serves the most accessible active copy, so the
  // asset is only blocked when every copy is unreadable.
  if (activeLinks.length === 0 || !activeLinks.every(linkIsUnreadable)) {
    return null;
  }

  return activeLinks.some(
    (link) => link.storage_tier_status === StorageTierStatus.REHYDRATING
  )
    ? "rehydrating"
    : "archived";
};

const DataAssetCard = ({
  dataAsset,
  sealogSearchTerm,
  sealogEventValues,
  sealogEventFreeTexts,
  sealogEventOptionNames,
  sealogEventOptionValues,
  regex,
  onPreviewOpen,
  isJustOpened = false,
}: {
  dataAsset: any;
  sealogSearchTerm: string;
  sealogEventValues: string[];
  sealogEventFreeTexts: string[];
  sealogEventOptionNames: string[];
  sealogEventOptionValues: string[];
  regex: boolean;
  onPreviewOpen?: (dataAsset: any, initialPreviewUrl?: string | null) => void;
  isJustOpened?: boolean;
}) => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const { assetType, canPreview: formatSupportsPreview } =
    assetIsPreviewable(dataAsset);
  const storageTierState = getStorageTierState(dataAsset);
  // Archived and rehydrating blobs are unreadable server-side; previewing
  // them would surface an opaque 409/read failure.
  const canPreview = formatSupportsPreview && storageTierState === null;
  const isVideo = assetType === "video/mp4";
  const downloadUrl = prepAssetDownloadURL(dataAsset);
  const {
    query: { data, isLoading },
    effectiveSealogSearchTerm,
    hasSealogFilters,
  } = useDataAssetSealogSearch({
    dataAssetPublicId: dataAsset.public_id,
    sealogSearchTerm,
    sealogEventValues,
    sealogEventFreeTexts,
    sealogEventOptionNames,
    sealogEventOptionValues,
    regex,
  });

  const handleDeploymentClick = () => {
    if (
      dataAsset.real_asset_deployments &&
      dataAsset.real_asset_deployments.length > 0
    ) {
      const realAssetId =
        dataAsset.real_asset_deployments[0].real_asset.public_id;
      const deploymentId = dataAsset.real_asset_deployments[0].public_id;
      navigate({ to: `/realassets/${realAssetId}/${deploymentId}` });
    }
  };

  const allTimestamps =
    isVideo && data?.items
      ? retrieveSealogTimestamps(data.items, dataAsset.path)
      : [];

  const handlePreviewToggle = () => {
    if (canPreview) {
      onPreviewOpen?.(dataAsset);
    }
  };

  const handleTimestampClick = (timestamp: string) => {
    const newPreviewUrl = `${prepAssetPreviewURL(dataAsset)}#t=${timestamp}`;
    onPreviewOpen?.(dataAsset, newPreviewUrl);
  };

  const hasDeploymentFeature = hasFeatureFlag(
    user,
    AvailableFeatureFlags.REAL_ASSET_DEPLOYMENT_DETAIL
  );

  return (
    <Card>
      <div className="flex justify-between">
        <div className="w-2 flex-1 overflow-hidden p-2 hover:overflow-auto">
          <h3
            className={`flex items-center text-lg font-semibold text-blue-200 ${
              canPreview ? "hover:cursor-pointer hover:underline" : ""
            }`}
            onClick={canPreview ? handlePreviewToggle : undefined}
            aria-label="Toggle file preview"
          >
            {iconForFileType(dataAsset.path)({ className: "mr-2 " })}
            {dataAsset.path}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-grey-200">
            <span>
              {formatUtcDate(dataAsset.asset_updated_dt)} &nbsp; | &nbsp;
              {prettyBytes(parseInt(dataAsset.size))}
            </span>
            {isJustOpened && (
              <span className="rounded bg-orange px-2 py-0.5 text-xs font-semibold text-blue-500">
                Last Viewed
              </span>
            )}
            {storageTierState === "archived" && (
              <span
                title="This asset is in archive storage and cannot be downloaded until rehydrated."
                className="rounded border border-amber-100 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700"
              >
                Archived
              </span>
            )}
            {storageTierState === "rehydrating" && (
              <span
                title="This asset is rehydrating from archive storage (takes 1–15 hours)."
                className="rounded border border-blue-100 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-300"
              >
                Rehydrating
              </span>
            )}
          </div>
          {hasSealogFilters && isLoading ? (
            <div className="space-y-2 pt-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : data && data.items.length > 0 ? (
            <MatchSealogEventsDisplay
              data={data}
              searchTerm={effectiveSealogSearchTerm}
              totalMatches={data.total}
              eventValues={sealogEventValues}
              eventFreeTexts={sealogEventFreeTexts}
              eventOptionNames={sealogEventOptionNames}
              eventOptionValues={sealogEventOptionValues}
              timestamps={allTimestamps}
              onTimestampClick={handleTimestampClick}
            />
          ) : (
            <p></p>
          )}
        </div>
        <div className="relative">
          <Menu as="div" className="relative">
            <Menu.Button
              aria-label="Menu"
              className="text-blue-200 focus:outline-none"
            >
              <FaEllipsisVertical className="ml-1 mt-2" />
            </Menu.Button>
            <Menu.Items className="absolute right-0 z-50 mt-2 w-48 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <Menu.Item>
                {({ active }) => (
                  <button
                    aria-label="Preview file"
                    className={`${
                      active ? "bg-gray-100" : ""
                    } group flex w-full items-center px-2 py-2 text-sm ${
                      canPreview
                        ? "text-blue-400"
                        : "cursor-not-allowed text-gray-400"
                    }`}
                    disabled={!canPreview}
                    onClick={handlePreviewToggle}
                  >
                    {canPreview ? (
                      <FaEye className="mr-2" size={16} />
                    ) : (
                      <FaEyeSlash className="mr-2" size={16} />
                    )}
                    Preview
                  </button>
                )}
              </Menu.Item>
              {dataAsset.real_asset_deployments &&
                dataAsset.real_asset_deployments.length > 0 && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        aria-label="See Deployment"
                        className={`${
                          active ? "bg-gray-100" : ""
                        } group flex w-full items-center px-2 py-2 text-left text-sm ${
                          hasDeploymentFeature
                            ? "text-blue-400"
                            : "cursor-not-allowed text-gray-400"
                        }`}
                        onClick={
                          hasDeploymentFeature
                            ? handleDeploymentClick
                            : undefined
                        }
                        disabled={!hasDeploymentFeature}
                      >
                        <FaLocationCrosshairs size={16} className="mr-2" />
                        {hasDeploymentFeature
                          ? "See Deployment"
                          : "Coming Soon"}
                      </button>
                    )}
                  </Menu.Item>
                )}
              <Menu.Item>
                {({ active }) =>
                  storageTierState !== null ? (
                    <button
                      aria-label="Download file"
                      disabled
                      title={
                        storageTierState === "archived"
                          ? "This asset is archived. Request rehydration to make it downloadable."
                          : "This asset is rehydrating from archive (takes 1–15 hours). Try again later."
                      }
                      className={`${
                        active ? "bg-gray-100" : ""
                      } group flex w-full cursor-not-allowed items-center px-2 py-2 text-sm text-gray-400`}
                    >
                      <FaDownload className="mr-2" />
                      {storageTierState === "archived"
                        ? "Archived"
                        : "Rehydrating"}
                    </button>
                  ) : (
                    <a
                      href={downloadUrl}
                      aria-label="Download file"
                      className={`${
                        active ? "bg-gray-100" : ""
                      } group flex w-full items-center px-2 py-2 text-sm text-blue-400`}
                    >
                      <FaDownload className="mr-2" />
                      Download
                    </a>
                  )
                }
              </Menu.Item>
            </Menu.Items>
          </Menu>
        </div>
      </div>
    </Card>
  );
};

export default DataAssetCard;
